// Interface por cima do mapa em tela cheia: cada canto tem uma função.
//   superior esquerdo  → de quem é a vez, rodada, fases e jogadores
//   superior direito   → missão, regiões, diário, regras e menu (abrem painéis)
//   inferior centro    → o que fazer agora (ações e decisões)
//   inferior esquerdo  → sua mão (cartas, loot e vocação)
//   inferior direito   → dados do último combate (some sozinho) e zoom
import { useEffect, useRef, useState } from 'react';
import { activePlayer, mustTrade, type PlayerView } from '@estandarte/engine';
import { icons, sigilOf } from '../art';
import type { LootStep } from '../lib/lootTargets';
import { useGame } from '../store/game';
import { useUi, type HudPanel } from '../store/ui';
import { bossName, chronicleOf, guildName, missionInfo, S, theme, vocationName } from '../theme';
import { RulesContent } from '../screens/About';
import { CardBack, CardInspector } from './Cards';
import { PlayerBio, PlayerMarks } from './PlayerBio';
import { Icon, type IconName } from '../ui/Icon';
import { DicePanel } from './Dice';
import type { Project } from './MapViewport';
import {
  ActionSection,
  BossDie,
  BossSection,
  HarmonyBeads,
  CardsSection,
  ControlsSection,
  Crest,
  LogSection,
  LootSection,
  PendingSection,
  Portrait,
  RegionsSection,
  VocationSection,
} from './Panels';

function TurnHud({ view }: { view: PlayerView }) {
  const { me, onlineSeats, mode } = useGame();
  const lastRoll = useGame((s) => s.lastBossRoll);
  const { bio, setBio } = useUi();
  const active = activePlayer(view);
  const idx = view.phase === 'over' ? view.result!.winner : active !== null && active >= 0 ? active : view.current;
  const p = view.players[idx];
  const phases = ['reinforce', 'attack', 'fortify'] as const;
  const bosses = view.info.ruleset.addons.bosses;
  return (
    <div className="hud-card turn-hud" aria-label="Vez e jogadores">
      <div className="turn-head">
        <Crest color={p.color} size={36} />
        {view.info.ruleset.addons.vocations && <Portrait vocation={p.vocation} size={36} />}
        <div style={{ minWidth: 0 }}>
          <div className="who">{p.name}</div>
          <div className="muted sub">
            {view.info.ruleset.addons.vocations && p.vocation ? `${vocationName(p.vocation)} · ` : ''}
            {guildName(p.color)}
            {p.kind === 'bot' ? ' · bot' : ''}
          </div>
        </div>
        <div className="spacer" />
        <div className="round">
          <div className="sc">Rodada</div>
          <div className="num">{view.round > 0 ? `${view.round}/${view.info.ruleset.serverSaveRound}` : '—'}</div>
        </div>
      </div>
      <div className="phase-track">
        {view.phase === 'setup' || view.phase === 'boss' || view.phase === 'over' ? (
          <span className="on">{S.phases[view.phase]}</span>
        ) : (
          phases.map((ph) => (
            <span key={ph} className={view.phase === ph ? 'on' : ''}>
              {S.phases[ph]}
            </span>
          ))
        )}
      </div>
      <EraLine view={view} />
      <div className="players" aria-label="Jogadores na ordem dos turnos">
        {view.players.map((pl) => {
          const seat = mode === 'online' ? onlineSeats[pl.id] : null;
          return (
            <button
              key={pl.id}
              className={`pl${pl.id === view.current ? ' active' : ''}${pl.alive ? '' : ' out'}${bio === pl.id ? ' open' : ''}`}
              onClick={() => setBio(pl.id)}
              aria-expanded={bio === pl.id}
              title={`Ver a ficha de ${pl.name}`}
            >
              <Crest color={pl.color} size={18} />
              <PlayerMarks view={view} id={pl.id} />
              <span className="pl-name">
                {pl.name}
                {pl.id === me && pl.name.toLowerCase() !== 'você' ? ' (você)' : ''}
                {seat && seat.kind === 'human' && !seat.connected ? ' (desconectado)' : ''}
                {seat?.botControlled ? ' · bot' : ''}
              </span>
              <span className="pl-counts">
                {pl.vocation === 'harmony' && view.info.ruleset.addons.vocations && (
                  <HarmonyBeads value={pl.harmony} max={view.info.ruleset.harmonyMax} small />
                )}
                <CardBack deck="territory" mini />
                <span className="num">{pl.cardCount}</span>
                <CardBack deck="loot" mini />
                <span className="num">{pl.lootCount}</span>
              </span>
            </button>
          );
        })}
      </div>
      {bosses && !view.boss && view.phase !== 'setup' && (
        <div className="boss-idle" title="No início de cada rodada, o dado do boss decide se um boss surge">
          {lastRoll ? <BossDie value={lastRoll} spawnFaces={view.info.ruleset.bossSpawnFaces} /> : <CardBack deck="boss" mini />}
          <span className="hint">
            Sem boss. Surge com {view.info.ruleset.bossSpawnFaces.join(' ou ')} no dado{lastRoll ? ` (último: ${lastRoll})` : ''}.
          </span>
        </div>
      )}
    </div>
  );
}

/** Era da partida e, quando vale, a trégua. */
function EraLine({ view }: { view: PlayerView }) {
  const chron = chronicleOf(view.info.ruleset.era);
  if (!chron) return null;
  const truce = view.info.ruleset.truceRounds;
  const truceOn = truce > 0 && view.round <= truce && view.phase !== 'setup';
  return (
    <div className="era-line" title={chron.rule}>
      <Icon name="scroll" size={14} />
      <span className="era-name">{chron.title}</span>
      {truceOn && <span className="tag truce">trégua até a rodada {truce}</span>}
    </div>
  );
}

const PANEL_TITLE: Record<HudPanel, string> = { regions: 'Regiões', log: 'Diário de campanha', rules: 'Regras', menu: 'Partida' };

function Toolbar({ view }: { view: PlayerView }) {
  const { me, mode, canUndo, undo } = useGame();
  const { panel, setPanel, toggleMission } = useUi();
  const mission = me !== null ? view.players[me]?.mission : null;
  const info = mission ? missionInfo(theme, mission) : null;
  const btn = (id: HudPanel, label: string, glyph: IconName) => (
    <button className={`tool${panel === id ? ' on' : ''}`} onClick={() => setPanel(id)} aria-expanded={panel === id} title={label}>
      <Icon name={glyph} />
      <span className="label">{label}</span>
    </button>
  );
  return (
    <div className="hud-tr">
      <div className="toolbar" role="toolbar" aria-label="Painéis da partida">
        {info && view.phase !== 'over' && (
          <button className="tool mission-tool" onClick={() => toggleMission(true)} title="Ver minha missão">
            <CardBack deck="mission" mini />
            <span className="label">
              <span className="sc">Missão</span> {info.name}
            </span>
          </button>
        )}
        {view.phase !== 'setup' && btn('regions', 'Regiões', 'regions')}
        {btn('log', 'Diário', 'log')}
        {btn('rules', 'Regras', 'rules')}
        {mode === 'local' && (
          <button className="tool" onClick={undo} disabled={!canUndo} title="Desfazer a última ação (não vale depois de rolar dados)">
            <Icon name="undo" />
            <span className="label">Desfazer</span>
          </button>
        )}
        {btn('menu', 'Menu', 'menu')}
      </div>
      {panel && (
        <div className={`hud-card hud-panel panel-${panel}`} role="dialog" aria-label={PANEL_TITLE[panel]}>
          <div className="panel-head">
            <h3>{PANEL_TITLE[panel]}</h3>
            <span className="spacer" />
            <button className="btn small ghost icon-btn" onClick={() => setPanel(null)} aria-label="Fechar painel">
              <Icon name="close" />
            </button>
          </div>
          <div className="panel-body">
            {panel === 'regions' && <RegionsSection view={view} />}
            {panel === 'log' && <LogSection />}
            {panel === 'rules' && (
              <div className="rules">
                <RulesContent />
              </div>
            )}
            {panel === 'menu' && (
              <>
                <ControlsSection />
                <p className="hint disclaimer">{S.disclaimer}</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Últimas linhas do diário, no alto, para acompanhar o que acontece sem abrir o painel. */
function Ticker() {
  const log = useGame((s) => s.log);
  const setPanel = useUi((s) => s.setPanel);
  const last = log.slice(-3);
  if (!last.length) return null;
  return (
    <button className="ticker" onClick={() => setPanel('log')} title="Abrir o diário de campanha" aria-hidden>
      {last.map((l, i) => (
        <span key={l.id} className={`line${i === last.length - 1 ? ' newest' : ''}`}>
          {l.text}
        </span>
      ))}
    </button>
  );
}

function HandDock({ view }: { view: PlayerView }) {
  const { me, legal } = useGame();
  const [open, setOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 760);
  const hand = me !== null ? view.players[me].cards ?? [] : [];
  const loot = me !== null ? view.players[me].loot ?? [] : [];
  const mustAct = me !== null && view.current === me && legal.some((a) => a.type === 'trade') && mustTrade(view, me);
  const overflow = view.pending?.kind === 'lootOverflow' && view.pending.player === me;
  useEffect(() => {
    if (mustAct || overflow) setOpen(true);
  }, [mustAct, overflow]);
  if (me === null || view.phase === 'setup') return null;
  return (
    <div className={`hud-bl hud-card hand-dock${open ? ' open' : ''}`}>
      <button className="hand-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        <CardBack deck="territory" mini />
        <span className="num">{hand.length}</span>
        {view.info.ruleset.addons.bosses && (
          <>
            <CardBack deck="loot" mini />
            <span className="num">{loot.length}</span>
          </>
        )}
        <span className="label">Sua mão</span>
        {mustAct && <span className="tag danger">troca obrigatória</span>}
        <span className="spacer" />
        <Icon name={open ? 'chevronDown' : 'chevronUp'} size={16} />
      </button>
      {open && (
        <div className="hand-body">
          <CardsSection view={view} legal={legal} />
          <LootSection view={view} legal={legal} />
          <VocationSection view={view} />
        </div>
      )}
    </div>
  );
}

/** Dados do último combate: aparece a cada rolagem e some depois de alguns segundos. */
function DiceDock() {
  const combatSeq = useGame((s) => s.combatSeq);
  const [visible, setVisible] = useState(false);
  const [hover, setHover] = useState(false);
  const first = useRef(combatSeq);
  useEffect(() => {
    if (combatSeq === first.current) return;
    setVisible(true);
    if (hover) return;
    const t = setTimeout(() => setVisible(false), 7000);
    return () => clearTimeout(t);
  }, [combatSeq, hover]);
  if (!visible) return null;
  return (
    <div className="hud-br hud-card dice-dock" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <DicePanel />
    </div>
  );
}

export function Hud({ step }: { step: LootStep | null }) {
  const { view, legal, me } = useGame();
  const hasBoss = !!view?.boss;
  // Esc fecha o painel aberto e o card do boss; o card fecha sozinho quando o boss sai do mapa.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      useUi.getState().setPanel(null);
      useUi.getState().setBossOpen(false);
      if (useUi.getState().bio !== null) useUi.getState().setBio(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    if (!hasBoss) useUi.getState().setBossOpen(false);
  }, [hasBoss]);
  if (!view) return null;
  return (
    <div className="hud">
      <div className="hud-tl">
        <TurnHud view={view} />
        <Ticker />
      </div>
      <Toolbar view={view} />
      <HandDock view={view} />
      {view.phase !== 'over' && (
        <div className="hud-bc action-dock">
          {view.pending && <PendingSection key={JSON.stringify(view.pending)} view={view} legal={legal} />}
          <ActionSection view={view} legal={legal} step={step} />
        </div>
      )}
      <PlayerBio view={view} me={me} />
      <DiceDock />
      <TurnBanner />
      <CardInspector />
    </div>
  );
}

/** Faixa que cruza o topo quando começa um turno (e a rodada). */
function TurnBanner() {
  const { lastEvents, eventSeq, view, me, fastBots } = useGame();
  const [banner, setBanner] = useState<{ seq: number; player: number; round: number | null } | null>(null);
  const seen = useRef(eventSeq);
  useEffect(() => {
    if (eventSeq === seen.current) return;
    seen.current = eventSeq;
    const turn = [...lastEvents].reverse().find((e) => e.type === 'turnStart');
    if (!turn || turn.type !== 'turnStart') return;
    const round = lastEvents.find((e) => e.type === 'roundStart');
    setBanner({ seq: eventSeq, player: turn.player, round: round?.type === 'roundStart' ? round.round : null });
  }, [eventSeq, lastEvents]);
  useEffect(() => {
    if (!banner || !view) return;
    const bot = view.players[banner.player]?.kind === 'bot';
    const t = setTimeout(() => setBanner(null), bot && fastBots ? 900 : banner.player === me ? 2000 : 1500);
    return () => clearTimeout(t);
  }, [banner, view, me, fastBots]);
  if (!banner || !view) return null;
  const p = view.players[banner.player];
  if (!p) return null;
  const mine = banner.player === me;
  return (
    <div key={banner.seq} className={`turn-banner${mine ? ' mine' : ''}`} role="status">
      {banner.round !== null && <span className="round sc">Rodada {banner.round}</span>}
      <span className="row-inner">
        <Crest color={p.color} size={34} />
        <span className="txt">{mine ? 'Sua vez' : `Vez de ${p.name}`}</span>
      </span>
    </div>
  );
}

/**
 * Card do boss preso ao covil no mapa. Fica escondido: aparece ao passar o mouse
 * no covil (ou no próprio card) e quando os detalhes estão abertos. Clicar no covil
 * ataca, quando dá; senão abre os detalhes.
 */
export function BossMapCard({
  view,
  project,
  scale,
  canAttack,
  onAttack,
}: {
  view: PlayerView;
  project: Project;
  scale: number;
  canAttack: boolean;
  onAttack: () => void;
}) {
  const { bossOpen, setBossOpen } = useUi();
  const [hover, setHover] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boss = view.boss;
  if (!boss) return null;
  const lair = theme.lairs[boss.region];
  const pos = project(lair.x, lair.y);
  const info = theme.bosses[boss.id];
  const below = pos.top < 330;
  /** Raio do covil com boss na tela (o desenho tem raio 17 no mapa, mais a margem do anel). */
  const r = Math.max(14, 24 * scale);
  const enter = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setHover(true);
  };
  const leave = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setHover(false), 180);
  };
  const shown = hover || bossOpen;
  return (
    <div
      key={boss.id + boss.region}
      className="boss-map-card map-overlay-ui"
      style={{ left: pos.left, top: pos.top, ['--boss-color' as string]: info?.color ?? '#A23B3B', ['--lair-r' as string]: `${r}px` }}
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      <button
        className="boss-hit"
        style={{ width: r * 2, height: r * 2 }}
        onClick={() => (canAttack ? onAttack() : setBossOpen(!bossOpen))}
        onFocus={enter}
        onBlur={leave}
        aria-label={`${bossName(boss.id)}, vida ${boss.hp} de ${boss.maxHp}, raid em ${boss.timer} rodada(s)${canAttack ? ': atacar' : ''}`}
      />
      {shown && (
        <button
          className={`boss-chip${canAttack ? ' attackable' : ''}`}
          onClick={() => setBossOpen(!bossOpen)}
          aria-expanded={bossOpen}
          title={`${bossName(boss.id)}: clique para ver os detalhes`}
        >
          <img src={sigilOf(boss.id)} alt="" width={30} height={30} />
          <span className="txt">
            <span className="name">{bossName(boss.id)}</span>
            <span className="stats">
              <img src={icons.heart} alt="" width={12} height={12} />
              <span className="num hp" key={boss.hp}>
                {boss.hp}/{boss.maxHp}
              </span>
              <span className="num timer" title="Rodadas até a raid">
                <Icon name="hourglass" size={12} />
                {boss.timer}
              </span>
            </span>
          </span>
        </button>
      )}
      {bossOpen && (
        <div className={`boss-pop${below ? ' below' : ''}`} role="dialog" aria-label={`Detalhes de ${bossName(boss.id)}`}>
          <button className="btn small ghost close icon-btn" onClick={() => setBossOpen(false)} aria-label="Fechar">
            <Icon name="close" />
          </button>
          <BossSection view={view} />
          {canAttack && (
            <button className="btn danger primary block" onClick={onAttack}>
              Atacar {bossName(boss.id)}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
